import {Injectable} from "@angular/core";
import {ClientConfiguration} from "../configuration/client.config";
import {IStudentSettings, ILibrarySelection, IAssignmentsResponse, IAssignment} from "../../library/library.objects";
import {SessionService} from "../configuration/session.service";
import {BookSessionType, ContentProgression} from "../../constants";
import {Observable} from "rxjs";
import {EventBus} from "../../event/EventBus";
import {NavigationEvent} from "../../event/NavigationEvent";
import {CompletionState} from "../usermodel/usermodel.objects";
import {StudentConfiguration} from "./student.configuration";
import {StepService} from "../step/step.service";
import {Login} from "../../login/login";
import {TranslateService} from "@ngx-translate/core";
import {TimerManagerService} from "../configuration/timer.manager.service";
import {UserModelService} from "../usermodel/usermodel.service";
import {PointService} from "../points/points.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {map} from "rxjs/operators";
import {UtilitiesService} from "../utilities.service";

export class SelectionsAndCategories {
    selections:ILibrarySelection[];
    categories:IAssignment[];
}

export enum ASSIGNMENT_STATUS {
    New,
    InProgress,
    Finished
}

@Injectable()
export class AssignmentService {

    private assignmentStatus:ASSIGNMENT_STATUS;
    protected _currentSelection:ILibrarySelection;
    private _currentSelectionGradeEquivFormat: number = null;
    private _isInitialized = false;
    private welcomeMessagePlayed = false;

    constructor(private http:HttpClient,
                private config:ClientConfiguration,
                private studentConfiguration: StudentConfiguration,
                private loginConfiguration: Login,
                private sessionService:SessionService,
                private stepService: StepService,
                private eventBus:EventBus,
                private translateService: TranslateService,
                private timerManagerService: TimerManagerService,
                private userModelService: UserModelService,
                private pointService: PointService) {
    }

    private getOptions() {
        return ({ withCredentials: true, headers: new HttpHeaders({"X-Forwarded-Host":this.config.slcReferrerDomain}) });
    }

    public retrieveAssignments():Observable<SelectionsAndCategories> {
        let url =
            this.config.isSuperUserMode ?
                "resources/superuser/superuserassignment.json" :
                 this.config.assignmentPath + this.loginConfiguration.learnerId + "?httpSessionId=" +
                 this.loginConfiguration.sessionId;
        return this.http.get(url, this.getOptions()).pipe(map(data => {
            let response:IAssignmentsResponse = data as IAssignmentsResponse;
            let selections:ILibrarySelection[] = AssignmentService.parseSelections(response.assignments);

            this.setStudentSetting(response.studentSettings);
            this.setupTranslationSettings();
            this.determineAssignmentStatus(selections);
            this.stepService.initialize();
            this.pointService.setLibraryPoints(response.totalPoint);

            if (!this.config.allowTimeoutStopMode && !this._isInitialized) {
                this.timerManagerService.startGlobalTimer();
            }

            this._isInitialized = true;
            return {selections: selections, categories: response.assignments};
        }));
    }

    protected static parseSelections(assignments:IAssignment[]):ILibrarySelection[] {
        return assignments.reduce((flatStories, assignment) => {
            // TODO: get topic field into the selection data instead of writing at runtime
            assignment.storyList.forEach((selection:ILibrarySelection) => selection.topic = assignment.title);
            return flatStories.concat(assignment.storyList);
        }, []);
    }

    private setupTranslationSettings(): void {
        if (this.studentConfiguration.isInstructionSpanishEnabled()) {
            this.translateService.use("es");
        } else {
            this.translateService.use("en");
        }
    }

    /**
     * start WordWall book session and load first page
     */
    public startNewSelection(selection: ILibrarySelection, gradeEquivForFormat: number):void {
        let selectionName: string = selection.storyDescription.referenceStory;

        let userModelObs: Observable<any> = new Observable(observer => {
            // I need to get an accurate user-model before I can figure out the current step
            this.userModelService.retrieveUserModel(selectionName).subscribe(() => {
                console.log("Completed fetching of user model for content-selection=", selectionName);
                observer.next();
                observer.complete();
            });
        });

        this.stepService.resetAccessTypes();
        userModelObs.subscribe(() => {
            let startStep = selection.currentStep ? selection.currentStep : this.userModelService.currentStep();
            this.sessionService.startBookSession(selection, startStep).subscribe(() => {
                this._currentSelection = selection;
                this._currentSelectionGradeEquivFormat = gradeEquivForFormat;
                this.eventBus.next(new NavigationEvent(["reading", selectionName]));
            });
        });

    }

    public getStartStep(selection: ILibrarySelection): BookSessionType {
        // basic idea here is to put a student-selected or locked mode user back in the step they should
        // be working on currently, even if that is not the step they were last in.  Doesn't apply
        // (we will not change the step) for unlocked users, completed selections, or if the user is not
        // in the step sequence yet/has not started the selection (e.g. they are in Word Wall.)
        if (this.studentConfiguration.contentProgression === ContentProgression.UNLOCKED
            || AssignmentService.isSelectionCompleted(selection)) {
            return selection.currentStep;
        }
        if (!selection.currentStep || (selection.currentStep === BookSessionType.WORDWALL)) {
            return selection.currentStep;
        }
        let startStep = BookSessionType.PREVIEW;
        if (selection.taqCompleteState === CompletionState.COMPLETED) {
            startStep = (selection.recordingCompleteState === CompletionState.COMPLETED) ?
                        BookSessionType.QUIZ : BookSessionType.FLUENCY;
        }
        return startStep;
    }

    private static isSelectionNew(selection:ILibrarySelection):boolean {
        if (!selection) {
            console.log("empty selection passed to isSelectionNew");
        }
        return !selection.storyCompleteState || selection.storyCompleteState === CompletionState.NOT_STARTED;
    }

    public static isSelectionCompleted(selection:ILibrarySelection):boolean {
        if (!selection) {
            console.log("empty selection passed to isSelectionCompleted");
        }
        return selection.storyCompleteState && (selection.storyCompleteState === CompletionState.COMPLETED);
    }

    protected determineAssignmentStatus(selections:ILibrarySelection[]):void {
        let isNew = true;
        let isAllFinished = true;
        this._currentSelection = undefined;
        for (let selection of selections) {
            if (!selection) {
                console.log("null selection in determineAssignmentStatus");
            }
            isNew = isNew && AssignmentService.isSelectionNew(selection);
            isAllFinished = isAllFinished && AssignmentService.isSelectionCompleted(selection);
            if (selection.isCurrent) {
                this._currentSelection = selection;
                this._currentSelectionGradeEquivFormat = UtilitiesService.determineATOSLevel(selection.storyDescription);
            }
            this.assignmentStatus = isNew ? ASSIGNMENT_STATUS.New :
                isAllFinished ? ASSIGNMENT_STATUS.Finished : ASSIGNMENT_STATUS.InProgress;
        }
    }

    public setStudentSetting(settings:IStudentSettings):void {
        this.studentConfiguration.isAudioAllowed = settings.quizAudio;
        this.studentConfiguration.instructionLanguage = settings.instructionLanguage;
        this.studentConfiguration.interventionWaitTime = settings.interventionDelaySetting;
        this.studentConfiguration.pronunciationCorrectness = settings.confidenceThreshold;
        this.studentConfiguration.contentProgression = <any>settings.libModeSelection;
        this.studentConfiguration.protocolDuration = settings.protocolDuration;
        this.studentConfiguration.glossaryAvailable = true;
        this.studentConfiguration.glossaryIsSpanishEnabled = settings.useAlternateLanguage;
    }

    public isAssignmentNew():boolean {
        return this.assignmentStatus === ASSIGNMENT_STATUS.New;
    }

    public isAssignmentFinished():boolean {
        return this.assignmentStatus === ASSIGNMENT_STATUS.Finished;
    }

    public get currentSelectionStatus():CompletionState {
        return this._currentSelection &&
            this._currentSelection.storyCompleteState || CompletionState.NOT_STARTED;
    }

    public isUnlocked(): boolean {
        return this.studentConfiguration.contentProgression === ContentProgression.UNLOCKED;
    }

    public isLocked(): boolean {
        return this.studentConfiguration.contentProgression === ContentProgression.LOCKED;
    }

    public isSelectionUnlocked(): boolean {
        return this.studentConfiguration.contentProgression === ContentProgression.STUDENT_SELECTED;
    }

    public isInitialized(): boolean {
        return this._isInitialized;
    }

    public canInitialize(): boolean {
        return !!this.loginConfiguration.learnerId;
    }

    public get currentSelection(): ILibrarySelection {
        return this._currentSelection;
    }

    public get currentSelectionGradeEquivFormat(): number {
        return this._currentSelectionGradeEquivFormat;
    }

    public set currentSelectionGradeEquivFormat(gradeEquiv) {
        this._currentSelectionGradeEquivFormat = gradeEquiv;
    }

    public welcomeMessageNotPlayed(): boolean {
        return !this.welcomeMessagePlayed;
    }

    public setWelcomeMessagePlayed(played: boolean) {
        this.welcomeMessagePlayed = played;
    }

}
